import {
  HttpInterceptorFn,
  provideHttpClient,
  withInterceptors,
} from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
  signal,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  withComponentInputBinding,
  withEnabledBlockingInitialNavigation,
  withPreloading,
} from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { ProvideEnvironment } from '@rxap/environment';
import { ProvideIconAssetPath } from '@rxap/icon';
import {
  provideExternalApps,
  RXAP_NAVIGATION_LAYOUT_CONFIG_DEFAULTS,
} from '@rxap/layout';
import {
  ErrorCaptureDialogService,
  HttpErrorInterceptor,
  provideAutoErrorInterception,
  withErrorCaptureDialogService,
} from '@rxap/ngx-error';
import { LanguageInterceptor } from '@rxap/ngx-localize';
import { ProvidePubSub } from '@rxap/ngx-pub-sub';
import { provideTheme, ThemeModeService } from '@rxap/ngx-theme';
import { provideTour } from '@rxap/ngx-tour';
import { injectOpenApiBaseUrlHttpInterceptorFn } from '@rxap/open-api/http-resource';
import {
  ProvideServiceWorkerUpdater,
  withSnackbarUpdater,
} from '@rxap/service-worker';
import { noop } from '@rxap/utilities';
import { provideOAuthClient } from 'angular-oauth2-oidc';
import { provideIndexedDb } from 'ngx-indexed-db';
import { provideMarkdown } from 'ngx-markdown';
import { environment } from '../environments/environment';
import { appRoutes } from './app.routes';
import { PreloadFlaggedStrategy } from './shared/routes/preload-flagged.strategy';
import { provideConfidentiality } from './shared/confidentiality/provide';
import { IpfsActivityService } from './shared/ipfs/ipfs-activity.service';
import { provideIpfsWorker } from './shared/ipfs/ipfs-worker.provider';
import { provideFlexsearchWorker } from './shared/search/flexsearch-worker.provider';
import { provideZipWorker } from './shared/zip/zip-worker.provider';
import { provideUserback } from './shared/init-userback';
import { providerQueryImageLoader } from './shared/query-image-loader';
import { provideArchiveSelectionStrategy } from './shared/archive-selection/archive-selection.strategy';
import { injectRequestApiAuthHttpInterceptorFn } from './shared/request-api-auth.http-interceptor';
import { provideRequestApi } from './shared/request-api/provide';
import { PortalArchiveSelectionStrategy } from './strategy/archive-selection/portal-archive-selection.strategy';
import { SearchService } from './shared/search/search.service';
import {
  provideIirdsStore,
  provideSearchService,
} from './shared/search/search.store';
import { iirdsStore } from './shared/stores/iirds.store';
import { storageQuotaStore } from './shared/stores/storage-quota.store';
import { STORAGE_QUOTA_ENFORCER } from './shared/storage-usage/storage-quota-enforcer';
import { iirdsExplorerTour } from './shared/components/iirds-explorer/iirds-explorer.tour';

const httpInterceptors: HttpInterceptorFn[] = [
  LanguageInterceptor,
  injectOpenApiBaseUrlHttpInterceptorFn,
  injectRequestApiAuthHttpInterceptorFn,
];

// httpInterceptors.push(HttpErrorInterceptor);

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    // eagerly construct IpfsActivityService so it registers as the IPFS
    // operation reporter from startup — before any route injects it — and never
    // misses activity. Cheap: its constructor does no I/O and does NOT bootstrap
    // the Helia node (that stays lazy until the first real operation).
    provideAppInitializer(() => {
      inject(IpfsActivityService);
    }),
    provideRouter(
      appRoutes,
      withEnabledBlockingInitialNavigation(),
      withComponentInputBinding(),
      withPreloading(PreloadFlaggedStrategy)
    ),
    provideAnimations(),
    environment.production
      ? []
      : provideAutoErrorInterception(
          withErrorCaptureDialogService(ErrorCaptureDialogService)
        ),
    ProvideEnvironment(environment),
    ProvidePubSub(),
    provideTheme(),
    provideExternalApps(),
    provideMarkdown(),
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.serviceWorker,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    ProvideServiceWorkerUpdater(withSnackbarUpdater()),
    ProvideIconAssetPath(['custom.svg']),
    provideHttpClient(withInterceptors(httpInterceptors)),
    // The Helia/libp2p node runs in a dedicated Web Worker so its DHT/bitswap
    // maintenance and synchronous block verification stay off the UI thread.
    // Registered at root because HeliaService is root-scoped and reached from
    // route guards. No worker is created when IPFS is disabled.
    provideIpfsWorker(),
    // FlexSearch index build/search runs in a dedicated Web Worker; only
    // spawned when `search.flexsearch.enabled` is set.
    provideFlexsearchWorker(),
    // Zip archive decompression (JSZip DEFLATE) runs in a dedicated Web
    // Worker; always available since zip-based package loading isn't behind
    // its own feature flag.
    provideZipWorker(),
    provideIirdsStore(iirdsStore),
    // Alias the enforcer token to the quota store so the archive caches can
    // trigger LRU eviction without importing the store (breaks the module cycle).
    { provide: STORAGE_QUOTA_ENFORCER, useExisting: storageQuotaStore },
    provideSearchService(SearchService),
    provideArchiveSelectionStrategy(PortalArchiveSelectionStrategy),
    provideUserback(),
    provideConfidentiality(),
    provideRequestApi(),
    // no resource-server config on purpose: tokens are attached only by the
    // request api auth interceptor, never automatically
    provideOAuthClient(),
    providerQueryImageLoader(),
    {
      provide: RXAP_NAVIGATION_LAYOUT_CONFIG_DEFAULTS,
      useValue: {
        collapsable: false,
        pinned: true,
        mode: 'side',
        opened: true,
      },
    },
    {
      provide: ThemeModeService,
      useValue: {
        activeTheme: signal('light'),
        themeSetting: signal('light'),
        darkMode: signal(false),
        toggleTheme: noop,
        setTheme: noop,
      },
    },
    provideIndexedDb({
      name: 'IPFS',
      version: 8,
      objectStoresMeta: [
        {
          store: 'content',
          storeConfig: { keyPath: 'key', autoIncrement: false },
          storeSchema: [
            { name: 'data', keypath: 'data', options: { unique: false } },
          ],
        },
        {
          store: 'upload-history',
          storeConfig: { keyPath: 'key', autoIncrement: false },
          storeSchema: [
            { name: 'name', keypath: 'name', options: { unique: false } },
            { name: 'date', keypath: 'date', options: { unique: false } },
            { name: 'type', keypath: 'type', options: { unique: false } },
            {
              name: 'folderKey',
              keypath: 'folderKey',
              options: { unique: false },
            },
          ],
        },
        {
          store: 'folder-handles',
          storeConfig: { keyPath: 'key', autoIncrement: false },
          storeSchema: [
            { name: 'name', keypath: 'name', options: { unique: false } },
            { name: 'handle', keypath: 'handle', options: { unique: false } },
          ],
        },
        {
          store: 'selected-archive',
          storeConfig: { keyPath: 'id', autoIncrement: false },
          storeSchema: [
            { name: 'type', keypath: 'type', options: { unique: false } },
            { name: 'key', keypath: 'key', options: { unique: false } },
            { name: 'handle', keypath: 'handle', options: { unique: false } },
          ],
        },
        {
          // Maps a container's root package IRI to the local archive source
          // that produced it, so a deep link can restore the right uploaded
          // or folder-synced archive after a full page reload.
          store: 'package-registry',
          storeConfig: { keyPath: 'packageIri', autoIncrement: false },
          storeSchema: [
            { name: 'type', keypath: 'type', options: { unique: false } },
            { name: 'key', keypath: 'key', options: { unique: false } },
            { name: 'handle', keypath: 'handle', options: { unique: false } },
            { name: 'name', keypath: 'name', options: { unique: false } },
            { name: 'date', keypath: 'date', options: { unique: false } },
          ],
        },
        {
          // User-configured request api server connections (the config.json
          // default is synthesized at runtime and never stored here).
          store: 'request-api-connections',
          storeConfig: { keyPath: 'id', autoIncrement: false },
          storeSchema: [
            { name: 'name', keypath: 'name', options: { unique: false } },
            { name: 'baseUrl', keypath: 'baseUrl', options: { unique: false } },
          ],
        },
        {
          // Metadata for request-api archives cached in OPFS (bytes live in
          // OPFS, mirroring the upload-history split-storage design).
          store: 'request-api-archives',
          storeConfig: { keyPath: 'key', autoIncrement: false },
          storeSchema: [
            {
              name: 'connectionId',
              keypath: 'connectionId',
              options: { unique: false },
            },
            {
              name: 'packageIri',
              keypath: 'packageIri',
              options: { unique: false },
            },
            { name: 'name', keypath: 'name', options: { unique: false } },
            { name: 'date', keypath: 'date', options: { unique: false } },
          ],
        },
        {
          // Holds the single non-extractable AES-GCM CryptoKey used to
          // encrypt request-api connection secrets at rest (see
          // RequestApiConnectionSecretService). Its raw bytes never leave
          // the browser's WebCrypto engine.
          store: 'request-api-secret-key',
          storeConfig: { keyPath: 'id', autoIncrement: false },
          storeSchema: [],
        },
        {
          // User-added IPFS CIDs that validated as iiRDS archives (metadata
          // only — the content is content-addressed and re-fetchable, so no
          // OPFS bytes needed). Keyed by the CID string.
          store: 'cid-history',
          storeConfig: { keyPath: 'key', autoIncrement: false },
          storeSchema: [
            { name: 'name', keypath: 'name', options: { unique: false } },
            { name: 'date', keypath: 'date', options: { unique: false } },
            { name: 'type', keypath: 'type', options: { unique: false } },
          ],
        },
      ],
    }),
  ],
};
